import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { enqueue } from "@/lib/offlineQueue";
import OfflineDataBanner from "@/components/OfflineDataBanner";
import { loadScaledCanvas, canvasToCompressedJpeg, dataUrlBytes, fmtKB } from "@/lib/imageCompress";
import { PageHeader, itemDaCategoria, nomeItem } from "@/components/pcp";
import { Camera, CheckCircle, X, MapPin, ArrowLeft, ArrowRight, PaintBrush } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Wizard de apontamento de PINTURA POR TRECHO.
 * Mobile-first. Passos:
 *  1) Equipe + OS + Item de pintura
 *  2) Foto + GPS
 *  3) Trecho (km inicial → km final)
 *  4) Método (mecânico ou manual)
 *  5) Tipo de pintura (grade grande)
 *  6) Cor + campos dinâmicos (largura, espessura, etc)
 *  7) Observação + salvar
 */

// yyyy-mm-dd de HOJE (timezone local) para input[type=date] e default.
const todayISODate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Combina data yyyy-mm-dd escolhida com a hora ATUAL local e retorna ISO string.
// Se retroativa, preserva a hora atual para ordenação estável.
const dateWithNowToIso = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return new Date().toISOString();
  const now = new Date();
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  const combined = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  return combined.toISOString();
};

// Padrões de pintura por MÉTODO (mecânico vs manual)
// modo determina o cálculo da área:
//   trecho        → extensao × largura × fator
//   trecho_largura→ extensao × largura × fator (mesmo trecho, largura obrigatória)
//   unidade_fixa  → quantidade × m2_por_unidade
//   faixa_pedestre→ largura_bastao × comprimento_bastao × qtd_bastoes
//   dim_livre     → largura × comprimento
//   legendas      → qtd_letras × altura_letra × largura_letra
const TIPOS_PINTURA_MECANICO = [
  { key: "continua_simples", label: "Contínua Simples", modo: "trecho", fator: 1.0, hint: "1 linha contínua" },
  { key: "continua_dupla", label: "Contínua Dupla", modo: "trecho", fator: 2.0, hint: "2 linhas paralelas" },
  { key: "seccionada_4x12", label: "Seccionada 4×12", modo: "trecho", fator: 0.25, hint: "4m pintado / 12m vão" },
  { key: "seccionada_4x8", label: "Seccionada 4×8", modo: "trecho", fator: 0.333, hint: "4m / 8m vão" },
  { key: "seccionada_4x4", label: "Seccionada 4×4", modo: "trecho", fator: 0.5, hint: "4m / 4m vão" },
  { key: "seccionada_1x1", label: "Seccionada 1×1", modo: "trecho", fator: 0.5, hint: "1m / 1m vão" },
  { key: "linha_bordo", label: "Linha de Bordo", modo: "trecho", fator: 1.0, hint: "borda contínua" },
  { key: "continua_mais_4x4", label: "Contínua Simples + 4×4", modo: "trecho", fator: 1.5, hint: "extensão × 1,5 × largura" },
  { key: "continua_mais_4x12", label: "Contínua Simples + 4×12", modo: "trecho", fator: 1.25, hint: "extensão × 1,25 × largura" },
];

const TIPOS_PINTURA_MANUAL = [
  { key: "pare", label: "Pare", modo: "unidade_fixa", m2_por_unidade: 3.84, hint: "3,84 m² por unidade" },
  { key: "de_preferencia", label: "Dê a Preferência", modo: "unidade_fixa", m2_por_unidade: 3.60, hint: "3,60 m² por unidade" },
  { key: "faixa_pedestre", label: "Faixa de Pedestre", modo: "faixa_pedestre", hint: "L bastão × comp × qtd bastões" },
  { key: "retencao", label: "Retenção", modo: "dim_livre", hint: "largura × comprimento" },
  { key: "zebrado", label: "Zebrado", modo: "dim_livre", hint: "largura × comprimento" },
  { key: "seta_reta", label: "Seta Reta", modo: "unidade_fixa", m2_por_unidade: 3.75, hint: "3,75 m² por unidade" },
  { key: "seta_vire", label: "Seta Vire À", modo: "unidade_fixa", m2_por_unidade: 4.75, hint: "4,75 m² por unidade" },
  { key: "seta_vire_siga", label: "Seta Vire À e Siga em Frente", modo: "unidade_fixa", m2_por_unidade: 6.25, hint: "6,25 m² por unidade" },
  { key: "seta_mof", label: "Seta MOF", modo: "unidade_fixa", m2_por_unidade: 13.00, hint: "13,00 m² por unidade" },
  { key: "legendas", label: "Legendas", modo: "legendas", hint: "qtd × altura × largura letra" },
  { key: "pne_idoso", label: "PNE / Idoso", modo: "unidade_fixa", m2_por_unidade: 2.88, hint: "2,88 m² por unidade" },
];

const CORES = [
  { key: "amarela", label: "Amarela", cor: "#FFCC00" },
  { key: "branca", label: "Branca", cor: "#F4F4F5" },
  { key: "vermelha", label: "Vermelha", cor: "#DC2626" },
  { key: "azul", label: "Azul", cor: "#2563EB" },
  { key: "preta", label: "Preta", cor: "#0A0A0A" },
];

const LARGURAS = [10, 12, 15, 20, 30, 45, 50];

// Utilitário: parse "0+450" ou "450" → metros
function kmToM(v) {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes("+")) {
    const [km, m] = s.split("+");
    const kmN = parseInt(km || "0", 10) || 0;
    const mN = parseInt(m || "0", 10) || 0;
    return kmN * 1000 + mN;
  }
  return parseFloat(s) || 0;
}

// Máscara para input: aceita apenas dígitos e "+"
function normalizeKm(v) {
  const clean = String(v || "").replace(/[^\d+]/g, "");
  return clean;
}

export default function ApontamentoPintura() {
  const [step, setStep] = useState(1);
  const [ordens, setOrdens] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [itens, setItens] = useState([]);

  // Passo 1: seleção — EQUIPE primeiro (obrigatório), depois OS (filtrada), depois ITEM
  const [selectedEquipe, setSelectedEquipe] = useState("");
  const [selectedOs, setSelectedOs] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  // Data do apontamento (padrão hoje; permite retroativo)
  const [dataExecucao, setDataExecucao] = useState(todayISODate());
  // Passo 2: trecho
  const [kmIni, setKmIni] = useState("");
  const [kmFim, setKmFim] = useState("");
  // Passo 3: método (mecânico ou manual)
  const [metodo, setMetodo] = useState(null); // "mecanico" | "manual"
  // Passo 4: tipo de pintura
  const [tipo, setTipo] = useState(null); // objeto de TIPOS_PINTURA
  // Passo 5: cor + campos dinâmicos conforme o modo do tipo
  const [cor, setCor] = useState(null);
  const [largura, setLargura] = useState(null); // usado em trecho e dim_livre
  const [quantidade, setQuantidade] = useState(""); // unidade_fixa
  const [larguraBastao, setLarguraBastao] = useState(""); // faixa_pedestre (cm)
  const [comprimentoBastao, setComprimentoBastao] = useState(""); // faixa_pedestre (m)
  const [qtdBastoes, setQtdBastoes] = useState(""); // faixa_pedestre
  const [comprimento, setComprimento] = useState(""); // dim_livre (m)
  const [qtdLetras, setQtdLetras] = useState(""); // legendas
  const [alturaLetra, setAlturaLetra] = useState(""); // legendas (m)
  const [larguraLetra, setLarguraLetra] = useState(""); // legendas (m)
  // Passo 6: foto + GPS
  const [fotos, setFotos] = useState([]);
  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  // Passo 7: observação + salvar
  const [observacao, setObservacao] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ordens").then((r) => setOrdens(r.data.filter((o) => o.status !== "Concluída" && o.status !== "Cancelada")));
    api.get("/equipes").then((r) => setEquipes(r.data));
  }, []);

  // Ao mudar de equipe, limpa a OS e o item (força escolher de novo dentro do escopo daquela equipe)
  useEffect(() => {
    setSelectedOs("");
    setSelectedItem("");
    setItens([]);
  }, [selectedEquipe]);

  useEffect(() => {
    if (!selectedOs) { setItens([]); setSelectedItem(""); return; }
    setSelectedItem("");
    api.get(`/itens?os_id=${selectedOs}`).then((r) => {
      // Só itens de categoria Horizontal (pintura)
      setItens(r.data.filter((i) => itemDaCategoria(i, "horizontal")));
    });
  }, [selectedOs]);

  // OS filtradas pela equipe selecionada (só as designadas àquela equipe)
  const ordensDaEquipe = ordens.filter((o) => !selectedEquipe || o.equipe_id === selectedEquipe);

  const currentItem = itens.find((i) => i.id === selectedItem);
  const extensao = useMemo(() => Math.max(0, kmToM(kmFim) - kmToM(kmIni)), [kmIni, kmFim]);
  const larguraM = (parseFloat(largura) || 0) / 100.0;

  // Cálculo dinâmico da área conforme o modo do tipo selecionado
  const area = useMemo(() => {
    if (!tipo) return 0;
    switch (tipo.modo) {
      case "trecho":
        return extensao * larguraM * (tipo.fator || 1);
      case "unidade_fixa":
        return (parseFloat(quantidade) || 0) * (tipo.m2_por_unidade || 0);
      case "faixa_pedestre":
        return ((parseFloat(larguraBastao) || 0) / 100.0) * (parseFloat(comprimentoBastao) || 0) * (parseFloat(qtdBastoes) || 0);
      case "dim_livre":
        return larguraM * (parseFloat(comprimento) || 0);
      case "legendas":
        return (parseFloat(qtdLetras) || 0) * (parseFloat(alturaLetra) || 0) * (parseFloat(larguraLetra) || 0);
      default:
        return 0;
    }
  }, [tipo, extensao, larguraM, quantidade, larguraBastao, comprimentoBastao, qtdBastoes, comprimento, qtdLetras, alturaLetra, larguraLetra]);

  // Lista de tipos conforme o método selecionado no Passo 3
  const tiposDisponiveis = metodo === "mecanico" ? TIPOS_PINTURA_MECANICO : metodo === "manual" ? TIPOS_PINTURA_MANUAL : [];

  const captureGeo = () => {
    if (!("geolocation" in navigator)) {
      toast.error("GPS não disponível");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoLoading(false);
        toast.success(`Localização OK (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => {
        setGeoLoading(false);
        toast.error(err.code === 1 ? "Permissão de GPS negada" : "Falha ao obter GPS");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Se ainda não capturou GPS, tenta capturar automático antes de estampar
    let currentGeo = geo;
    if (!currentGeo && "geolocation" in navigator) {
      try {
        currentGeo = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            }),
            reject,
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
          );
        });
        setGeo(currentGeo);
      } catch { /* segue sem GPS */ }
    }

    for (const f of files) {
      try {
        const stamped = await stampImage(f, currentGeo);
        setFotos((p) => [...p, stamped]);
      } catch (err) { toast.error(`Falha ao processar ${f.name}: ${err?.message || err}`); }
    }
  };

  // Compõe overlay estilo Timestamp Camera sobre a foto (data, hora, GPS, categoria, tipo, km)
  // Formato limpo — texto branco com contorno preto no canto inferior esquerdo
  const stampImage = async (file, geoData) => {
        const canvas = await loadScaledCanvas(file);
        const ctx = canvas.getContext("2d");

        const W = canvas.width;
        const H = canvas.height;

        // Formata data e hora
        const now = new Date();
        const pad2 = (n) => String(n).padStart(2, "0");
        const dt = `${pad2(now.getDate())}/${pad2(now.getMonth() + 1)}/${now.getFullYear()} ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

        // Formata GPS no padrão S/N e W/E com vírgula decimal
        const fmtCoord = (v, pos, neg) => {
          const abs = Math.abs(v).toFixed(6).replace(".", ",");
          return `${abs}${v < 0 ? neg : pos}`;
        };
        const gpsText = geoData
          ? `${fmtCoord(geoData.latitude, "N", "S")} ${fmtCoord(geoData.longitude, "E", "W")}`
          : "GPS: nao capturado";

        // Nome da categoria em pt-BR
        const categoriaLabel = "SINALIZAÇÃO HORIZONTAL";
        // Tipo específico (ex.: "SECCIONADA 4×12") — usa tipo.label ou description do item
        const tipoLabel = (tipo?.label || currentItem?.descricao || "").toUpperCase();
        // KM (só o inicial, formato pedido)
        const kmLabel = kmIni ? `KM ${kmIni}` : "";

        const linhas = [dt, gpsText, categoriaLabel, tipoLabel, kmLabel].filter(Boolean);

        // Tamanho da fonte proporcional (~3.5% da altura)
        const fontSize = Math.max(22, Math.round(H * 0.033));
        const lineHeight = Math.round(fontSize * 1.15);
        const pad = Math.round(fontSize * 0.6);

        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
        ctx.textBaseline = "bottom";
        ctx.textAlign = "left";

        // Desenha da última linha para cima, começando no rodapé com padding
        let y = H - pad;
        for (let i = linhas.length - 1; i >= 0; i--) {
          const txt = linhas[i];
          // Contorno preto para legibilidade
          ctx.lineWidth = Math.max(3, Math.round(fontSize * 0.18));
          ctx.strokeStyle = "rgba(0,0,0,0.85)";
          ctx.strokeText(txt, pad, y);
          // Preenchimento branco
          ctx.fillStyle = "#FFFFFF";
          ctx.fillText(txt, pad, y);
          y -= lineHeight;
        }

        return canvasToCompressedJpeg(canvas);
  };

  const canNext = () => {
    if (step === 1) return selectedEquipe && selectedOs && selectedItem;
    if (step === 2) return fotos.length > 0; // foto obrigatória logo após seleção
    if (step === 3) {
      // Km é obrigatório em todos os modos (mantém regra anterior).
      return kmIni && kmFim;
    }
    if (step === 4) return !!metodo;
    if (step === 5) return !!tipo;
    if (step === 6) {
      if (!cor) return false;
      if (!tipo) return false;
      switch (tipo.modo) {
        case "trecho": return !!largura;
        case "unidade_fixa": return parseFloat(quantidade) > 0;
        case "faixa_pedestre": return parseFloat(larguraBastao) > 0 && parseFloat(comprimentoBastao) > 0 && parseFloat(qtdBastoes) > 0;
        case "dim_livre": return !!largura && parseFloat(comprimento) > 0;
        case "legendas": return parseFloat(qtdLetras) > 0 && parseFloat(alturaLetra) > 0 && parseFloat(larguraLetra) > 0;
        default: return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!currentItem) return;
    setLoading(true);
    try {
      const payload = {
        os_id: selectedOs,
        item_id: selectedItem,
        equipe_id: selectedEquipe || null,
        quantidade: area,
        observacao,
        fotos,
        data_execucao: dateWithNowToIso(dataExecucao),
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        gps_accuracy: geo?.accuracy ?? null,
        pintura: {
          km_inicial: kmIni,
          km_final: kmFim,
          metodo: metodo,
          tipo: tipo.key,
          tipo_label: tipo.label,
          modo: tipo.modo,
          fator: tipo.fator || null,
          m2_por_unidade: tipo.m2_por_unidade || null,
          largura_cm: parseFloat(largura) || null,
          cor: cor.key,
          quantidade: parseFloat(quantidade) || null,
          largura_bastao_cm: parseFloat(larguraBastao) || null,
          comprimento_bastao_m: parseFloat(comprimentoBastao) || null,
          qtd_bastoes: parseFloat(qtdBastoes) || null,
          comprimento_m: parseFloat(comprimento) || null,
          qtd_letras: parseFloat(qtdLetras) || null,
          altura_letra_m: parseFloat(alturaLetra) || null,
          largura_letra_m: parseFloat(larguraLetra) || null,
          area_m2_cliente: area,
        },
      };

      // Tenta enviar direto; se falhar por rede/servidor, salva na fila offline
      const isNetworkError = (e) => !navigator.onLine || !e?.response || e?.code === "ERR_NETWORK";
      try {
        await api.post("/apontamentos", payload);
        toast.success(`Apontamento salvo: ${area.toFixed(2)} m² pintados`);
      } catch (err) {
        if (isNetworkError(err)) {
          await enqueue("/apontamentos", payload);
          toast.info(`Sem internet · Apontamento (${area.toFixed(2)} m²) salvo no celular. Vai enviar sozinho quando voltar o sinal.`);
        } else {
          throw err;
        }
      }
      // Reset (mantém equipe selecionada para agilizar próximo apontamento na mesma frente)
      setStep(1);
      setKmIni(""); setKmFim(""); setMetodo(null); setTipo(null); setCor(null); setLargura(null);
      setQuantidade(""); setLarguraBastao(""); setComprimentoBastao(""); setQtdBastoes("");
      setComprimento(""); setQtdLetras(""); setAlturaLetra(""); setLarguraLetra("");
      setFotos([]); setGeo(null); setObservacao("");
      setDataExecucao(todayISODate());
      // Reload item (só se online — offline não faz sentido)
      if (navigator.onLine) {
        try {
          const r = await api.get(`/itens?os_id=${selectedOs}`);
          setItens(r.data.filter((i) => itemDaCategoria(i, "horizontal")));
        } catch { /* silent */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`Erro ao salvar${detail ? ": " + detail : ""}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        testid="page-apontamento-pintura"
        title="Apontamento de Pintura"
        subtitle={`Passo ${step} de 7 — registrar trecho`}
      />
      <div className="page-body" style={{ maxWidth: 640, margin: "0 auto", width: "100%", paddingBottom: 96 }}>
        <OfflineDataBanner />
        {/* Barra de progresso */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {[1,2,3,4,5,6,7].map((n) => (
            <div key={n} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: n <= step ? "#0A0A0A" : "#E4E4E7",
              transition: "background 0.2s",
            }} />
          ))}
        </div>

        {/* PASSO 1 — Equipe + OS + Item */}
        {step === 1 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 1</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Qual OS e serviço?</h2>
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="label">Equipe <span style={{ color: "#DC2626" }}>*</span></label>
                <select
                  className="select input-lg"
                  value={selectedEquipe}
                  onChange={(e) => setSelectedEquipe(e.target.value)}
                  data-testid="ap-p-equipe"
                >
                  <option value="">— Selecione a equipe —</option>
                  {equipes.map((eq) => <option key={eq.id} value={eq.id}>{eq.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Ordem de Serviço <span style={{ color: "#DC2626" }}>*</span></label>
                <select
                  className="select input-lg"
                  value={selectedOs}
                  onChange={(e) => setSelectedOs(e.target.value)}
                  disabled={!selectedEquipe}
                  data-testid="ap-p-os"
                >
                  <option value="">
                    {selectedEquipe ? "— Selecione a OS —" : "— Selecione a equipe primeiro —"}
                  </option>
                  {ordensDaEquipe.map((o) => (
                    <option key={o.id} value={o.id}>{o.numero} · {o.contratante} · {o.rodovia}</option>
                  ))}
                </select>
                {selectedEquipe && ordensDaEquipe.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#B45309", marginTop: 8 }}>
                    Não há OS ativas designadas a esta equipe. Vincule uma OS à equipe em <strong>Ordens de Serviço</strong>.
                  </div>
                )}
              </div>
              <div>
                <label className="label">Item de pintura (categoria Horizontal) <span style={{ color: "#DC2626" }}>*</span></label>
                <select
                  className="select input-lg"
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                  disabled={!selectedOs}
                  data-testid="ap-p-item"
                >
                  <option value="">
                    {selectedOs ? "— Selecione o item —" : "— Selecione a OS primeiro —"}
                  </option>
                  {itens.map((i) => (
                    <option key={i.id} value={i.id}>{nomeItem(i)} · resta {(i.qtd_planejada - i.qtd_executada).toFixed(0)} m²</option>
                  ))}
                </select>
                {selectedOs && itens.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#B45309", marginTop: 8 }}>
                    Esta OS ainda não tem itens de categoria <strong>Horizontal</strong>. Adicione um item de pintura primeiro.
                  </div>
                )}
              </div>
              <div>
                <label className="label">
                  Data do apontamento
                  {dataExecucao !== todayISODate() && (
                    <span
                      data-testid="ap-p-data-retroativo-badge"
                      style={{
                        marginLeft: 8,
                        padding: "2px 6px",
                        background: "#FFCC00",
                        color: "#0A0A0A",
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        borderRadius: 3,
                        letterSpacing: "0.04em",
                      }}
                    >
                      RETROATIVO
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  className="input input-lg"
                  value={dataExecucao}
                  max={todayISODate()}
                  onChange={(e) => setDataExecucao(e.target.value || todayISODate())}
                  data-testid="ap-p-data"
                  style={{ fontSize: "1.1rem", fontWeight: 600 }}
                />
                <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 6 }}>
                  Padrão: hoje. Altere para registrar produção de um dia anterior.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASSO 3 — Trecho km */}
        {step === 3 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 3</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Qual o trecho?</h2>
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="label">Km inicial (formato: 0+000)</label>
                <input
                  className="input input-lg" inputMode="text" placeholder="Ex.: 0+000"
                  value={kmIni} onChange={(e) => setKmIni(normalizeKm(e.target.value))}
                  data-testid="ap-p-km-ini"
                  style={{ fontSize: "1.4rem", fontWeight: 600 }}
                />
              </div>
              <div>
                <label className="label">Km final (formato: 0+000)</label>
                <input
                  className="input input-lg" inputMode="text" placeholder="Ex.: 0+450"
                  value={kmFim} onChange={(e) => setKmFim(normalizeKm(e.target.value))}
                  data-testid="ap-p-km-fim"
                  style={{ fontSize: "1.4rem", fontWeight: 600 }}
                />
              </div>
              <div style={{ padding: 16, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3 }}>
                <div className="overline">Extensão do trecho</div>
                <div className="font-mono font-semibold" style={{ fontSize: "1.8rem" }}>
                  {extensao.toLocaleString("pt-BR")} <span style={{ fontSize: "1rem", color: "#71717A" }}>metros</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASSO 4 — Método (mecânico ou manual) */}
        {step === 4 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 4</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>O serviço é mecânico ou manual?</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <button
                onClick={() => setMetodo("mecanico")}
                data-testid="metodo-mecanico"
                style={{
                  padding: "24px 16px",
                  border: metodo === "mecanico" ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                  background: metodo === "mecanico" ? "#0A0A0A" : "#FFF",
                  color: metodo === "mecanico" ? "#FFCC00" : "#0A0A0A",
                  borderRadius: 4, cursor: "pointer",
                  minHeight: 130,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <div style={{ fontSize: "1.8rem" }}>🚜</div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>MECÂNICO</div>
                <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>Máquina autopropelida</div>
              </button>
              <button
                onClick={() => setMetodo("manual")}
                data-testid="metodo-manual"
                style={{
                  padding: "24px 16px",
                  border: metodo === "manual" ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                  background: metodo === "manual" ? "#0A0A0A" : "#FFF",
                  color: metodo === "manual" ? "#FFCC00" : "#0A0A0A",
                  borderRadius: 4, cursor: "pointer",
                  minHeight: 130,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <div style={{ fontSize: "1.8rem" }}>🖌️</div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>MANUAL</div>
                <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>Aplicação manual/rolo</div>
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: "0.75rem", color: "#71717A" }}>
              O método vai constar no BDM entregue ao cliente.
            </div>
          </div>
        )}

        {/* PASSO 5 — Tipo de pintura */}
        {step === 5 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 5 · {metodo?.toUpperCase()}</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Qual o tipo de serviço?</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {tiposDisponiveis.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTipo(t)}
                  data-testid={`tipo-${t.key}`}
                  style={{
                    padding: 14,
                    border: tipo?.key === t.key ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                    background: tipo?.key === t.key ? "#0A0A0A" : "#FFF",
                    color: tipo?.key === t.key ? "#FFCC00" : "#0A0A0A",
                    borderRadius: 4, cursor: "pointer", textAlign: "left",
                    minHeight: 88,
                  }}
                >
                  <div style={{ fontSize: "0.95rem", fontWeight: 700, marginBottom: 2 }}>{t.label}</div>
                  <div style={{ fontSize: "0.72rem", opacity: 0.8 }}>{t.hint}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PASSO 6 — Cor + campos dinâmicos (variam pelo modo) */}
        {step === 6 && tipo && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 6 · {tipo.label}</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Cor e dimensões</h2>

            <label className="label">Cor da tinta</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(85px, 1fr))", gap: 8, marginBottom: 16 }}>
              {CORES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCor(c)}
                  data-testid={`cor-${c.key}`}
                  style={{
                    padding: 12, border: cor?.key === c.key ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                    background: "#FFF", borderRadius: 4, cursor: "pointer", textAlign: "center",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.cor, border: c.key === "branca" ? "1px solid #E4E4E7" : "none" }} />
                  <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>{c.label}</div>
                </button>
              ))}
            </div>

            {/* MODO: TRECHO — largura da faixa */}
            {tipo.modo === "trecho" && (
              <>
                <label className="label">Largura (cm)</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(65px, 1fr))", gap: 6 }}>
                  {LARGURAS.map((l) => (
                    <button
                      key={l}
                      onClick={() => setLargura(l)}
                      data-testid={`largura-${l}`}
                      style={{
                        padding: "12px 6px", border: largura === l ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                        background: largura === l ? "#0A0A0A" : "#FFF", color: largura === l ? "#FFCC00" : "#0A0A0A",
                        borderRadius: 4, cursor: "pointer", fontWeight: 700,
                      }}
                    >{l}cm</button>
                  ))}
                </div>
                <input type="number" placeholder="ou digite outra largura em cm"
                  className="input" style={{ marginTop: 8 }} inputMode="numeric"
                  value={largura && !LARGURAS.includes(largura) ? largura : ""}
                  onChange={(e) => setLargura(parseFloat(e.target.value) || null)}
                />
              </>
            )}

            {/* MODO: UNIDADE FIXA — só qtd (Pare, Setas, PNE, etc) */}
            {tipo.modo === "unidade_fixa" && (
              <>
                <label className="label">Quantidade de unidades</label>
                <input type="number" min="1" step="1" inputMode="numeric"
                  className="input input-lg" style={{ fontSize: "1.4rem", fontWeight: 600 }}
                  placeholder="Ex.: 2"
                  value={quantidade} onChange={(e) => setQuantidade(e.target.value)}
                  data-testid="ap-p-qtd"
                />
                <div style={{ marginTop: 8, padding: 10, background: "#FAFAFA", borderRadius: 3, fontSize: "0.8rem" }}>
                  <strong>{tipo.m2_por_unidade.toLocaleString("pt-BR")} m²</strong> por unidade × <strong>{parseFloat(quantidade) || 0}</strong> = <strong style={{ color: "#059669" }}>{((parseFloat(quantidade) || 0) * tipo.m2_por_unidade).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} m²</strong>
                </div>
              </>
            )}

            {/* MODO: FAIXA DE PEDESTRE — L bastão × comp × qtd */}
            {tipo.modo === "faixa_pedestre" && (
              <>
                <label className="label">Largura do bastão (cm)</label>
                <input type="number" inputMode="numeric" className="input"
                  placeholder="Ex.: 40" value={larguraBastao}
                  onChange={(e) => setLarguraBastao(e.target.value)} data-testid="ap-p-largura-bastao"
                />
                <label className="label" style={{ marginTop: 8 }}>Comprimento de cada bastão (m)</label>
                <input type="number" step="0.01" inputMode="decimal" className="input"
                  placeholder="Ex.: 4" value={comprimentoBastao}
                  onChange={(e) => setComprimentoBastao(e.target.value)} data-testid="ap-p-comprimento-bastao"
                />
                <label className="label" style={{ marginTop: 8 }}>Quantidade de bastões</label>
                <input type="number" min="1" step="1" inputMode="numeric" className="input"
                  placeholder="Ex.: 8" value={qtdBastoes}
                  onChange={(e) => setQtdBastoes(e.target.value)} data-testid="ap-p-qtd-bastoes"
                />
              </>
            )}

            {/* MODO: DIM LIVRE — Retenção / Zebrado */}
            {tipo.modo === "dim_livre" && (
              <>
                <label className="label">Largura da faixa (cm)</label>
                <input type="number" inputMode="numeric" className="input"
                  placeholder="Ex.: 40" value={largura || ""}
                  onChange={(e) => setLargura(parseFloat(e.target.value) || null)}
                  data-testid="ap-p-largura-livre"
                />
                <label className="label" style={{ marginTop: 8 }}>Comprimento (m)</label>
                <input type="number" step="0.01" inputMode="decimal" className="input"
                  placeholder="Ex.: 12" value={comprimento}
                  onChange={(e) => setComprimento(e.target.value)}
                  data-testid="ap-p-comprimento"
                />
              </>
            )}

            {/* MODO: LEGENDAS — qtd letras × altura × largura */}
            {tipo.modo === "legendas" && (
              <>
                <label className="label">Quantidade de letras/dígitos</label>
                <input type="number" min="1" step="1" inputMode="numeric" className="input"
                  placeholder="Ex.: 4" value={qtdLetras}
                  onChange={(e) => setQtdLetras(e.target.value)} data-testid="ap-p-qtd-letras"
                />
                <label className="label" style={{ marginTop: 8 }}>Altura da letra (m)</label>
                <input type="number" step="0.01" inputMode="decimal" className="input"
                  placeholder="Ex.: 1,60" value={alturaLetra}
                  onChange={(e) => setAlturaLetra(e.target.value)} data-testid="ap-p-altura-letra"
                />
                <label className="label" style={{ marginTop: 8 }}>Largura da letra (m)</label>
                <input type="number" step="0.01" inputMode="decimal" className="input"
                  placeholder="Ex.: 0,60" value={larguraLetra}
                  onChange={(e) => setLarguraLetra(e.target.value)} data-testid="ap-p-largura-letra"
                />
              </>
            )}
          </div>
        )}

        {/* PASSO 2 — Foto + GPS (registrado logo após seleção da OS) */}
        {step === 2 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 2</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 6 }}>Foto e localização</h2>
            <div style={{ fontSize: "0.85rem", color: "#71717A", marginBottom: 12 }}>
              A foto é obrigatória para comprovação da mudança de trecho.
            </div>
            <div style={{ padding: 10, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 3, fontSize: "0.78rem", color: "#7A5D00", marginBottom: 14 }}>
              <strong>✨ Timestamp automático:</strong> data/hora, coordenadas GPS, categoria, tipo e km são estampados na foto — sem cliente questionar depois.
            </div>
            <div>
              <label htmlFor="pintura-photo" className="btn btn-outline btn-lg btn-block" style={{ marginBottom: 12 }}>
                <Camera size={20} weight="bold" /> {fotos.length === 0 ? "Tirar foto do trecho" : `Adicionar mais fotos (${fotos.length})`}
              </label>
              <input id="pintura-photo" type="file" accept="image/*" capture="environment" multiple onChange={handlePhotos} style={{ display: "none" }} data-testid="ap-p-fotos" />
              {fotos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 16 }}>
                  {fotos.map((f, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img src={f} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 4 }} />
                      <button onClick={() => setFotos(fotos.filter((_, ix) => ix !== i))} style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "#FFF", border: 0, borderRadius: "50%", width: 22, height: 22, cursor: "pointer" }}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              {fotos.length > 0 && (
                <div data-testid="ap-p-fotos-tamanho" style={{ fontSize: "0.72rem", color: "#71717A", marginTop: -8, marginBottom: 14 }}>
                  {fotos.length} foto{fotos.length > 1 ? "s" : ""} · {fmtKB(fotos.reduce((s, f) => s + dataUrlBytes(f), 0))} · comprimidas para envio rápido
                </div>
              )}
              <button
                type="button" onClick={captureGeo} disabled={geoLoading} data-testid="ap-p-gps"
                className="btn btn-outline btn-lg btn-block"
                style={{ background: geo ? "#ECFDF5" : "#FAFAFA", borderColor: geo ? "#059669" : undefined, color: geo ? "#065F46" : undefined }}
              >
                <MapPin size={20} weight="bold" />
                {geoLoading ? "Obtendo GPS…" : geo ? `GPS OK · ±${Math.round(geo.accuracy)}m` : "Capturar GPS do local"}
              </button>
            </div>
          </div>
        )}

        {/* PASSO 7 — Revisão */}
        {step === 7 && currentItem && tipo && cor && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 7</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Confira e envie</h2>

            <div style={{ padding: 16, background: "#FFFBEA", border: "2px solid #FFCC00", borderRadius: 4, marginBottom: 14 }}>
              <div className="overline">Área calculada</div>
              <div className="font-mono font-semibold" style={{ fontSize: "2rem", lineHeight: 1.1 }}>
                {area.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} <span style={{ fontSize: "1rem", color: "#71717A" }}>m²</span>
              </div>
              <div className="font-mono" style={{ fontSize: "0.78rem", color: "#52525B", marginTop: 4 }}>
                {tipo.modo === "trecho" && `${extensao.toLocaleString("pt-BR")}m × ${larguraM.toFixed(2)}m × fator ${tipo.fator.toFixed(2)}`}
                {tipo.modo === "unidade_fixa" && `${quantidade || 0} un × ${tipo.m2_por_unidade.toLocaleString("pt-BR")} m²/un`}
                {tipo.modo === "faixa_pedestre" && `${(parseFloat(larguraBastao)||0)/100}m × ${comprimentoBastao}m × ${qtdBastoes} bastões`}
                {tipo.modo === "dim_livre" && `${((parseFloat(largura)||0)/100).toFixed(2)}m × ${comprimento}m`}
                {tipo.modo === "legendas" && `${qtdLetras} letras × ${alturaLetra}m × ${larguraLetra}m`}
              </div>
            </div>

            <table className="pcp-table" style={{ marginBottom: 12 }}>
              <tbody>
                <tr><td style={{ color: "#71717A" }}>OS</td><td style={{ fontWeight: 600, textAlign: "right" }}>{ordens.find((o) => o.id === selectedOs)?.numero}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Item</td><td style={{ fontWeight: 600, textAlign: "right" }}>{currentItem.descricao}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Trecho</td><td style={{ fontWeight: 600, textAlign: "right" }}>km {kmIni} → {kmFim}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Método</td><td style={{ fontWeight: 600, textAlign: "right", textTransform: "uppercase" }}>{metodo}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Tipo</td><td style={{ fontWeight: 600, textAlign: "right" }}>{tipo.label}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Cor</td><td style={{ fontWeight: 600, textAlign: "right" }}>{cor.label}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Fotos</td><td style={{ fontWeight: 600, textAlign: "right" }}>{fotos.length}</td></tr>
                <tr><td style={{ color: "#71717A" }}>GPS</td><td style={{ fontWeight: 600, textAlign: "right" }}>{geo ? "OK" : "Não capturado"}</td></tr>
              </tbody>
            </table>

            <label className="label">Observação (opcional)</label>
            <textarea className="textarea" rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: trecho em curva, tempo bom" />
          </div>
        )}

        {/* Navegação */}
        <div className="wizard-actions" style={{ display: "flex", gap: 8, marginTop: 16 }} data-testid="pin-wizard-actions">
          {step > 1 && (
            <button className="btn btn-outline btn-lg" style={{ flex: 1 }} onClick={() => setStep(step - 1)} data-testid="btn-voltar">
              <ArrowLeft size={16} weight="bold" /> Voltar
            </button>
          )}
          {step < 7 && (
            <button
              className="btn btn-primary btn-lg" style={{ flex: 2 }}
              disabled={!canNext()} onClick={() => setStep(step + 1)}
              data-testid="btn-avancar"
            >
              Avançar <ArrowRight size={16} weight="bold" />
            </button>
          )}
          {step === 7 && (
            <button
              className="btn btn-primary btn-lg" style={{ flex: 2 }}
              disabled={loading} onClick={submit}
              data-testid="btn-salvar-pintura"
            >
              <CheckCircle size={20} weight="bold" /> {loading ? "Salvando…" : "Salvar trecho"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
